import { useEffect, useState } from 'react';

import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  RetailEvotorAccount,
  RetailEvotorAccountCreateRequest,
  RetailEvotorAccountUpdateRequest,
} from '../../model/settingsTypes';

export interface RetailEvotorAccountFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  /** The account being edited; `null` in create mode. */
  account: RetailEvotorAccount | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (body: RetailEvotorAccountCreateRequest | RetailEvotorAccountUpdateRequest) => void;
}

/**
 * Create/edit dialog for an Evotor account — modeled structurally on `ProviderFormDialog` (a
 * blocking-while-saving `Dialog` with the same submit/cancel chrome), but **not** built on it or
 * on `MaskedCredentialField`: this API has no `{present, masked}` credential and no
 * blank-keeps-existing update semantics — `token` is always required, including on edit. Only the
 * reveal-adornment pattern is borrowed inline from `MaskedCredentialField`.
 */
export function RetailEvotorAccountFormDialog({
  open,
  mode,
  account,
  saving,
  onClose,
  onSubmit,
}: RetailEvotorAccountFormDialogProps) {
  const { t } = useTranslation('settings');
  const [name, setName] = useState('');
  const [evotorId, setEvotorId] = useState('');
  const [token, setToken] = useState('');
  const [active, setActive] = useState(true);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    if (open) {
      setName(account?.name ?? '');
      setEvotorId(account?.evotorId ?? '');
      setToken('');
      setActive(account?.active ?? true);
      setReveal(false);
    }
  }, [open, account]);

  const nameValid = name.trim() !== '';
  const evotorIdValid = mode === 'edit' || evotorId.trim() !== '';
  const tokenValid = token.trim() !== '';
  const canSubmit = nameValid && evotorIdValid && tokenValid;

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    if (mode === 'create') {
      const body: RetailEvotorAccountCreateRequest = {
        active,
        evotorId: evotorId.trim(),
        name: name.trim(),
        token: token.trim(),
      };
      onSubmit(body);
    } else {
      // The row `Switch` (a token-free PATCH) owns `active` for edits — the dialog silently
      // resends the account's current value since the PUT body requires it.
      const body: RetailEvotorAccountUpdateRequest = {
        active: account?.active ?? true,
        name: name.trim(),
        token: token.trim(),
      };
      onSubmit(body);
    }
  };

  const revealAdornment = (
    <InputAdornment position="end">
      <IconButton
        edge="end"
        size="small"
        aria-label={
          reveal
            ? t('retailSettings.accountDialog.hideToken')
            : t('retailSettings.accountDialog.showToken')
        }
        onClick={() => setReveal((value) => !value)}
        disabled={saving}
      >
        {reveal ? (
          <VisibilityOffRoundedIcon fontSize="small" />
        ) : (
          <VisibilityRoundedIcon fontSize="small" />
        )}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === 'create'
          ? t('retailSettings.accountDialog.createTitle')
          : t('retailSettings.accountDialog.editTitle')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {mode === 'edit' ? (
            <Alert severity="info">{t('retailSettings.accountDialog.tokenReentryNotice')}</Alert>
          ) : null}

          <TextField
            label={t('retailSettings.accountDialog.nameLabel')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            size="small"
            fullWidth
            required
            disabled={saving}
            error={!nameValid && name !== ''}
            helperText={!nameValid ? t('retailSettings.accountDialog.nameRequired') : undefined}
          />

          {mode === 'create' ? (
            <TextField
              label={t('retailSettings.accountDialog.evotorIdLabel')}
              value={evotorId}
              onChange={(event) => setEvotorId(event.target.value)}
              size="small"
              fullWidth
              required
              disabled={saving}
              helperText={t('retailSettings.accountDialog.evotorIdHelper')}
              slotProps={{ htmlInput: { sx: { fontFamily: 'JetBrains Mono, monospace' } } }}
            />
          ) : (
            <Stack spacing={0.25}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('retailSettings.accountDialog.evotorIdLabel')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {account?.evotorId}
              </Typography>
            </Stack>
          )}

          <TextField
            label={t('retailSettings.accountDialog.tokenLabel')}
            value={token}
            onChange={(event) => setToken(event.target.value)}
            type={reveal ? 'text' : 'password'}
            size="small"
            fullWidth
            required
            disabled={saving}
            autoComplete="new-password"
            placeholder={
              mode === 'edit' ? t('retailSettings.accountDialog.tokenEditPlaceholder') : undefined
            }
            helperText={
              mode === 'edit' ? t('retailSettings.accountDialog.tokenEditHelper') : undefined
            }
            slotProps={{ input: { endAdornment: revealAdornment } }}
          />

          {mode === 'create' ? (
            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={(event) => setActive(event.target.checked)}
                  disabled={saving}
                />
              }
              label={t('retailSettings.accountDialog.activeLabel')}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !canSubmit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mode === 'create'
            ? t('retailSettings.accountDialog.submitCreate')
            : t('retailSettings.accountDialog.submitSave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
